// PWA installability helper for the admin area.
//
// The admin manifest is intentionally NOT referenced in /public/index.html
// because the public site must not advertise the admin PWA. Instead we
// inject the <link rel="manifest"> dynamically the moment the admin app
// mounts, and strip it on unmount. We also expose a tiny observable so
// a React component can show an "Install app" button when the browser
// has decided the page is installable (fires `beforeinstallprompt`).

const MANIFEST_HREF = "/admin.webmanifest";
const THEME = "#8a6a2d";

let deferredPrompt = null;        // the native BeforeInstallPromptEvent
const listeners = new Set();      // subscribers that want availability updates

function notify() {
  const available = !!deferredPrompt;
  listeners.forEach((cb) => { try { cb(available); } catch {} });
}

function onBeforeInstallPrompt(e) {
  e.preventDefault();              // keep Chrome from showing its default mini-infobar
  deferredPrompt = e;
  notify();
}

function onAppInstalled() {
  deferredPrompt = null;
  notify();
}

/** Must be called once from the admin app root (idempotent). */
export function enableAdminPWA() {
  if (typeof document === "undefined") return;
  // 1) Dynamically inject the manifest link (and matching theme meta).
  if (!document.querySelector('link[rel="manifest"][data-admin-pwa]')) {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = MANIFEST_HREF;
    link.dataset.adminPwa = "1";
    document.head.appendChild(link);
  }
  if (!document.querySelector('meta[name="theme-color"][data-admin-pwa]')) {
    const meta = document.createElement("meta");
    meta.name = "theme-color";
    meta.content = THEME;
    meta.dataset.adminPwa = "1";
    document.head.appendChild(meta);
  }
  // iOS has its own non-standard add-to-home-screen flow; a few meta tags
  // (apple-mobile-web-app-capable + title + status-bar-style) make the
  // installed app look like a native app on iPhone/iPad.
  if (!document.querySelector('meta[name="apple-mobile-web-app-capable"][data-admin-pwa]')) {
    const m1 = document.createElement("meta");
    m1.name = "apple-mobile-web-app-capable";
    m1.content = "yes";
    m1.dataset.adminPwa = "1";
    document.head.appendChild(m1);
    const m2 = document.createElement("meta");
    m2.name = "apple-mobile-web-app-status-bar-style";
    m2.content = "black-translucent";
    m2.dataset.adminPwa = "1";
    document.head.appendChild(m2);
    const m3 = document.createElement("meta");
    m3.name = "apple-mobile-web-app-title";
    m3.content = "Admin Panel";
    m3.dataset.adminPwa = "1";
    document.head.appendChild(m3);
  }

  // 2) Listen for the install prompt event. Only wire once.
  if (!window.__adminPWABound) {
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    window.__adminPWABound = true;
  }
}

/** Subscribe to install-availability changes. Returns an unsubscribe fn. */
export function onInstallAvailable(cb) {
  listeners.add(cb);
  // Fire the current state immediately so UI starts in sync.
  cb(!!deferredPrompt);
  return () => listeners.delete(cb);
}

/** True when the app is already running as an installed PWA. */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)")?.matches;
  // iOS Safari uses navigator.standalone instead of display-mode.
  return !!mq || !!window.navigator?.standalone;
}

/** Triggers the native install prompt. Resolves to the user's choice. */
export async function requestInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  notify();
  try {
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    return choice;              // { outcome: "accepted" | "dismissed", platform }
  } catch (e) {
    return { outcome: "error", error: String(e) };
  }
}
